// React
import { useState, useEffect } from 'react'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

export default function usePurchaseOrder(initialId = null) {
    // Initial state
    const [purchaseOrder, setPurchaseOrder] = useState({
        name: '',
        reference: '',
        supplier: null,
        lines: [],
        attachments: [],
        location_id: 1,
    })

    // API
    const { requestGet, requestPost, requestPatch } = useAxiosApi()

    // Load the purchase order
    const fetchInitialPurchaseOrder = async () => {
        const { data } = await requestGet(`/purchase-orders/${initialId}`)

        if (data) {
            setPurchaseOrder({
                id: data.id,
                reference: data.reference,
                status: data.status,
                name: data.name,
                supplier: data.supplier,
                lines: data.lines.map((line) => ({
                    id: line.id,
                    component_id: line.component_id,
                    component: line.component,
                    quantity_to_receive: line.quantity_to_receive,
                    reception_at: line.reception_at,
                    quantity_received: line.quantity_received,
                })),
                attachments: data.attachments,
                canceled_at: data.canceled_at,
                closed_at: data.closed_at,
                location_id: data.location_id,
                location: data.location,
            })
        }
    }

    useEffect(() => {
        if (initialId) {
            fetchInitialPurchaseOrder()
        }
    }, [initialId])

    // Reference
    // The reference of the purchase order is a string that can only contain the following characters: space, A-Z, a-z, 0-9, -, /, and is limited to 20 characters. It is also uppercase.
    // It is provided by the supplier and is used to identify the purchase order.
    const onReferenceChange = (e) => {
        // Remove all char that are note : space, A-Z, a-z, 0-9, -, /
        let value = e.target.value.replace(/[^A-Za-z0-9\s\-\/]/g, '')

        // Remove any space char
        value = value.replace(/\s/g, '')

        // Uppercase
        value = value.toUpperCase()

        // Max 20 char
        value = value.substring(0, 20)

        setPurchaseOrder((prev) => ({
            ...prev,
            reference: value,
        }))
    }

    // Supplier
    const onSupplierChange = (supplier) => {
        setPurchaseOrder((prev) => ({
            ...prev,
            supplier,
        }))
    }

    // Name change
    const onNameChange = (e) => {
        setPurchaseOrder((prev) => ({
            ...prev,
            name: e.target.value,
        }))
    }

    // Add a line
    const addLine = (component_to_add) => {
        // Check if the line already exists
        const existingLine = purchaseOrder.lines.find(
            (line) =>
                line.component_id === component_to_add.id &&
                line.reception_at === null
        )

        if (!existingLine) {
            setPurchaseOrder((prev) => ({
                ...prev,
                lines: [
                    ...prev.lines,
                    {
                        component_id: component_to_add.id,
                        quantity_to_receive: component_to_add.quantity_to_add,
                        quantity_received: 0,
                        reception_at: null,
                        component: {
                            id: component_to_add.id,
                            name: component_to_add.name,
                            component_category:
                                component_to_add.component_category,
                        },
                    },
                ],
            }))
        } else {
            // If the line already exists, we increment the quantity to receive
            setPurchaseOrder((prev) => ({
                ...prev,
                lines: prev.lines.map((line) =>
                    line === existingLine
                        ? {
                              ...line,
                              quantity_to_receive:
                                  line.quantity_to_receive +
                                  component_to_add.quantity_to_add,
                          }
                        : line
                ),
            }))
        }
    }

    // Delete a line
    const deleteLine = (line) => {
        const newLines = purchaseOrder.lines.filter(
            (l) =>
                !(
                    l.component_id === line.component_id &&
                    l.reception_at === line.reception_at
                )
        )

        setPurchaseOrder((prev) => ({
            ...prev,
            lines: newLines,
        }))
    }

    const computeLineChangeReceptionDate = (lines, line, receptionDate) => {
        // Check if their is already a line with the new reception date
        const existingLineIndex = lines.find(
            (l) =>
                l.component_id === line.component_id &&
                l.reception_at === receptionDate
        )

        if (existingLineIndex) {
            // If there is already a line with the new reception date, we merge the quantity and delete the line

            const newLines = lines
                .filter((l) => l !== line)
                .map((l) =>
                    l === existingLineIndex
                        ? {
                              ...l,
                              quantity_received:
                                  l.quantity_received + line.quantity_received,
                              quantity_to_receive:
                                  l.quantity_to_receive +
                                  line.quantity_to_receive,
                          }
                        : l
                )

            return newLines
        } else {
            // If there is no line with the new reception date, we just change the reception date of the line

            const newLines = lines.map((l) =>
                l === line ? { ...l, reception_at: receptionDate } : l
            )

            return newLines
        }
    }

    // Change reception date of the line
    const changeLineReceptionDate = (line, receptionDate) => {
        const newLines = computeLineChangeReceptionDate(
            purchaseOrder.lines,
            line,
            receptionDate
        )

        setPurchaseOrder((prev) => ({
            ...prev,
            lines: newLines,
        }))
    }

    // Change reception date of all lines of designated existing date
    const changeDateGroup = (previousReceptionDate, newReceptionDate) => {
        // Merge any quantity if there is already a line with the new reception date
        let newLines = purchaseOrder.lines

        purchaseOrder.lines.forEach((line) => {
            const isSameDate =
                line.reception_at === previousReceptionDate ||
                (line.reception_at === null && previousReceptionDate === 'null')

            if (isSameDate) {
                newLines = computeLineChangeReceptionDate(
                    newLines,
                    line,
                    newReceptionDate
                )
            }
        })

        setPurchaseOrder((prev) => ({
            ...prev,
            lines: newLines,
        }))
    }

    // Change line quantity to receive
    const changeLineQuantityToReceive = (line, quantity) => {
        setPurchaseOrder((prev) => ({
            ...prev,
            lines: prev.lines.map((l) =>
                l === line ? { ...l, quantity_to_receive: quantity } : l
            ),
        }))
    }

    // Save the purchase order — the API writes the order and its lines in one
    // transaction, so a half-saved order is no longer possible.
    const [isSaving, setIsSaving] = useState(false)

    const save = async () => {
        setIsSaving(true)

        try {
            const { data } = await requestPost('/purchase-orders', {
                ...(initialId && { id: initialId }),
                reference: purchaseOrder.reference,
                supplier_id: purchaseOrder.supplier.id,
                name: purchaseOrder.name,
                location_id: purchaseOrder.location_id,
                lines: purchaseOrder.lines.map((line) => ({
                    ...(line.id && { id: line.id }),
                    component_id: line.component_id,
                    quantity_received: line.quantity_received,
                    quantity_to_receive: line.quantity_to_receive,
                    reception_at: line.reception_at,
                })),
            })

            // Refetch in edit mode only: on creation there is no id to load
            // yet, and the caller redirects to the new order anyway.
            if (initialId) {
                await fetchInitialPurchaseOrder()
            }

            return {
                id: data.id,
                mode: initialId ? 'update' : 'create',
            }
        } finally {
            setIsSaving(false)
        }
    }

    // Change status. The canceled_at / closed_at stamps are set server-side.
    const changeStatus = async (status) => {
        await requestPatch(`/purchase-orders/${purchaseOrder.id}/status`, {
            status,
        })

        // Refetch the purchase order
        fetchInitialPurchaseOrder()
    }

    // Change location
    const onLocationIdChange = (location_id) => {
        setPurchaseOrder((prev) => ({
            ...prev,
            location_id,
        }))
    }

    // Handlers
    const handlers = {
        onReferenceChange,
        onNameChange,
        onSupplierChange,
        addLine,
        deleteLine,
        changeLineReceptionDate,
        changeLineQuantityToReceive,
        changeDateGroup,
        save,
        onLocationIdChange,
    }

    return {
        purchaseOrder,
        setPurchaseOrder,
        handlers,
        isSaving,
        refetch: fetchInitialPurchaseOrder,
        changeStatus,
    }
}
